import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import {
  Wrench, CheckCircle2, XCircle, ExternalLink, Building2, Tag, Globe, Phone, Mail, MapPin,
  Calendar, FileCheck, Loader2, X, Send, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_PILLS = {
  pending: 'bg-amber-500/20 text-amber-200 border border-amber-500/30',
  approved: 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30',
  denied: 'bg-rose-500/20 text-rose-200 border border-rose-500/30',
  appealed: 'bg-blue-500/20 text-blue-200 border border-blue-500/30',
};

export const ServiceProviderApplicationsSection = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [denying, setDenying] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/service-provider/applications', { params: filter === 'all' ? {} : { status: filter } });
      setApps(res.data.applications || []);
    } catch { toast.error('Failed to load applications'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (appId) => {
    setActing(appId);
    try {
      await api.post(`/admin/service-provider/applications/${appId}/approve`);
      toast.success('Application approved');
      await load();
    } catch { toast.error('Could not approve'); }
    finally { setActing(null); }
  };

  const sendDeny = async () => {
    if (!denying) return;
    if (denyReason.trim().length < 10) { toast.error('Please provide a reason of at least 10 characters'); return; }
    setActing(denying.id);
    try {
      await api.post(`/admin/service-provider/applications/${denying.id}/deny`, { reason: denyReason.trim() });
      toast.success('Application denied — partner notified');
      setDenying(null); setDenyReason('');
      await load();
    } catch { toast.error('Could not deny'); }
    finally { setActing(null); }
  };

  const counts = {
    all: apps.length,
    pending: apps.filter(a => a.status === 'pending').length,
    appealed: apps.filter(a => a.status === 'appealed').length,
    approved: apps.filter(a => a.status === 'approved').length,
    denied: apps.filter(a => a.status === 'denied').length,
  };

  return (
    <div data-testid="admin-sp-section">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2"><Wrench className="h-5 w-5 text-sky-400" /> Service Provider Applications</h2>
        <p className="text-sm text-slate-400">Review Verified Service Provider submissions, approve or deny with feedback.</p>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {['all','pending','appealed','approved','denied'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${filter === s ? 'bg-sky-500 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10 border border-white/10'}`}
            data-testid={`sp-admin-filter-${s}`}>
            {s} {counts[s] > 0 && <span className="ml-1 opacity-70">({counts[s]})</span>}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-sky-400" /></div>
       : apps.length === 0 ? (
         <div className="text-center py-12 text-slate-400 bg-white/5 rounded-xl border border-white/10">
           <Wrench className="h-10 w-10 mx-auto mb-3 text-slate-600" />
           <p className="text-sm">No applications in this view.</p>
         </div>
       ) : (
         <div className="space-y-4">
           {apps.map(app => (
             <div key={app.id} className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid={`sp-admin-app-${app.id}`}>
               <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
                 <div>
                   <div className="flex items-center gap-2 mb-1">
                     <h3 className="font-bold text-white text-lg">{app.company_name}</h3>
                     <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${STATUS_PILLS[app.status] || ''}`}>{app.status}</span>
                   </div>
                   <p className="text-xs text-slate-400">Submitted {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : '—'} · by {app.user_name} ({app.user_email})</p>
                 </div>
                 <div className="flex gap-2">
                   {(app.status === 'pending' || app.status === 'appealed') && (
                     <>
                       <button onClick={() => approve(app.id)} disabled={acting === app.id}
                         className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-md disabled:opacity-50" data-testid={`sp-approve-${app.id}`}>
                         <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                       </button>
                       <button onClick={() => { setDenying(app); setDenyReason(''); }} disabled={acting === app.id}
                         className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold rounded-md disabled:opacity-50" data-testid={`sp-deny-${app.id}`}>
                         <XCircle className="h-3.5 w-3.5" /> Deny
                       </button>
                     </>
                   )}
                 </div>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                 <Field icon={Building2} label="Company" value={app.company_name} />
                 <Field icon={Calendar} label="Years in Business" value={app.years_in_business} />
                 <Field icon={Tag} label="Service Category" value={app.service_category} />
                 <Field icon={MapPin} label="Coverage Area" value={app.coverage_area} />
                 <Field icon={Globe} label="Website" value={app.website_url} href={app.website_url?.startsWith('http') ? app.website_url : `https://${app.website_url}`} />
                 <Field icon={Phone} label="Phone" value={app.phone_number} />
                 <Field icon={Mail} label="Email" value={app.email_address} />
                 <Field icon={MapPin} label="Business Address" value={app.business_address || '—'} />
                 <Field icon={Phone} label="Contact Information" value={app.contact_information} wide />
                 <Field icon={FileCheck} label="Professional Licenses" value={app.professional_licenses} wide />
                 <Field icon={Building2} label="Business Description" value={app.business_description} wide />
               </div>

               {app.status === 'denied' && app.denial_reason && (
                 <div className="mt-4 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
                   <p className="text-[10px] uppercase tracking-wider text-rose-300 font-bold mb-1">Denial Reason</p>
                   <p className="text-sm text-rose-100">{app.denial_reason}</p>
                 </div>
               )}

               {app.status === 'appealed' && app.appeal_message && (
                 <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2">
                   <p className="text-[10px] uppercase tracking-wider text-blue-300 font-bold mb-1 flex items-center gap-1"><MessageSquare className="h-3 w-3" /> Appeal Message</p>
                   <p className="text-sm text-blue-100 mb-2">{app.appeal_message}</p>
                   {app.denial_reason && <p className="text-[11px] text-blue-300/80">Previous denial: {app.denial_reason}</p>}
                 </div>
               )}
             </div>
           ))}
         </div>
       )}

      {denying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setDenying(null)}>
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <h3 className="font-bold text-white">Deny Application</h3>
                <p className="text-xs text-slate-400 mt-0.5">Explain why so the provider can improve their application.</p>
              </div>
              <button onClick={() => setDenying(null)} className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg" data-testid="sp-deny-modal-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6">
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-4 text-sm text-slate-300">
                Visible to <span className="font-semibold text-white">{denying.user_name}</span> on next sign-in.
              </div>
              <textarea value={denyReason} onChange={(e) => setDenyReason(e.target.value)} rows={6}
                placeholder="Explain the reason for denial — be specific so the applicant can address it..."
                className="w-full px-3 py-2.5 rounded-md bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 text-sm" data-testid="sp-deny-reason" />
              <p className="text-xs text-slate-500 mt-1">{denyReason.length} characters</p>
            </div>
            <div className="px-6 py-4 border-t border-white/10 bg-black/20 flex justify-end gap-2 rounded-b-2xl">
              <button onClick={() => setDenying(null)} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancel</button>
              <button onClick={sendDeny} disabled={acting === denying.id}
                className="inline-flex items-center gap-1.5 px-5 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-md font-semibold text-sm disabled:opacity-50" data-testid="sp-deny-send">
                {acting === denying.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Denial
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ icon: Icon, label, value, href, wide }) => (
  <div className={`bg-white/5 border border-white/10 rounded-lg p-3 ${wide ? 'md:col-span-2' : ''}`}>
    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</p>
    {href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline inline-flex items-center gap-1 break-all">{value} <ExternalLink className="h-3 w-3 flex-shrink-0" /></a>
    ) : (
      <p className="text-sm text-slate-100 whitespace-pre-wrap break-words">{value || '—'}</p>
    )}
  </div>
);
